import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_LEADERSHIP_LENS_ID,
  resolveLeadershipLens,
  type LeadershipLensId,
} from "@/lib/leadershipLens";
import {
  resolveSoloModeTrialState,
  SOLO_TRIAL_LIMITS,
} from "@/lib/soloModeTrial";
import { TRANSCRIPT_ANALYZED_STATUS_ALIASES } from "@/lib/transcriptStatus";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

export const SOLO_MODE_EXPERIENCE_QUERY_KEY = "solo-mode-experience-state";

export interface ResolveSoloModeExperienceStateInput {
  accountMode?: string | null;
  trialStartedAt?: string | null;
  trialEndsAt?: string | null;
  hasCoachConnection: boolean;
  leadershipLens?: string | null;
  transcriptUploadsUsed?: number | null;
  processedMeetingsUsed?: number | null;
  now?: Date;
}

export interface SoloModeExperienceState {
  isSoloModeLeader: boolean;
  isSoloTrialActive: boolean;
  isSoloTrialExpired: boolean;
  hasCoachConnection: boolean;
  accountMode: string | null;
  leadershipLens: LeadershipLensId;
  leadershipLensLabel: string;
  daysRemaining: number | null;
  transcriptUploadsRemaining: number | null;
  processedMeetingsRemaining: number | null;
  isLoading: boolean;
}

export const DEFAULT_SOLO_MODE_EXPERIENCE_STATE: SoloModeExperienceState = {
  isSoloModeLeader: false,
  isSoloTrialActive: false,
  isSoloTrialExpired: false,
  hasCoachConnection: false,
  accountMode: null,
  leadershipLens: DEFAULT_LEADERSHIP_LENS_ID,
  leadershipLensLabel: "Coach Veera Default",
  daysRemaining: null,
  transcriptUploadsRemaining: null,
  processedMeetingsRemaining: null,
  isLoading: false,
};

export function resolveSoloModeExperienceState(
  input: ResolveSoloModeExperienceStateInput,
): SoloModeExperienceState {
  const trialState = resolveSoloModeTrialState({
    accountMode: input.accountMode ?? null,
    trialStartedAt: input.trialStartedAt ?? null,
    trialEndsAt: input.trialEndsAt ?? null,
    hasCoachConnection: input.hasCoachConnection,
    now: input.now,
  });
  const selectedLens = resolveLeadershipLens(input.leadershipLens);
  const isSoloModeLeader = input.accountMode === "solo_trial" && !input.hasCoachConnection;

  return {
    isSoloModeLeader,
    isSoloTrialActive: trialState.isSoloTrialActive,
    isSoloTrialExpired: trialState.isSoloTrialExpired,
    hasCoachConnection: input.hasCoachConnection,
    accountMode: input.accountMode ?? null,
    leadershipLens: selectedLens.id,
    leadershipLensLabel: selectedLens.label,
    daysRemaining: trialState.daysRemaining,
    transcriptUploadsRemaining: buildRemainingCount(
      isSoloModeLeader,
      input.transcriptUploadsUsed,
      SOLO_TRIAL_LIMITS.transcriptUploads,
    ),
    processedMeetingsRemaining: buildRemainingCount(
      isSoloModeLeader,
      input.processedMeetingsUsed,
      SOLO_TRIAL_LIMITS.processedMeetings,
    ),
    isLoading: false,
  };
}

export function useSoloModeExperienceState(): SoloModeExperienceState {
  const { user, loading: authLoading } = useAuth();
  const isPreviewUser = user?.id === PREVIEW_LEADER_USER_ID;
  const query = useQuery({
    queryKey: [SOLO_MODE_EXPERIENCE_QUERY_KEY, user?.id],
    enabled: Boolean(user?.id) && !authLoading && !isPreviewUser,
    staleTime: 60_000,
    queryFn: async () => {
      const userId = user!.id;
      const [
        profileResult,
        coachLinkResult,
        onboardingResult,
        transcriptUploadsResult,
        processedMeetingsResult,
      ] = await Promise.all([
        supabase
          .from("profiles")
          .select("account_mode, trial_started_at, trial_ends_at")
          .eq("id", userId)
          .maybeSingle(),
        supabase
          .from("coach_leader_links")
          .select("coach_id")
          .eq("leader_id", userId)
          .maybeSingle(),
        supabase
          .from("leader_onboarding")
          .select("leadership_lens")
          .eq("leader_id", userId)
          .maybeSingle(),
        supabase
          .from("transcript_uploads")
          .select("id", { count: "exact", head: true })
          .eq("leader_id", userId),
        supabase
          .from("transcript_uploads")
          .select("meeting_id")
          .eq("leader_id", userId)
          .in("analysis_status", TRANSCRIPT_ANALYZED_STATUS_ALIASES),
      ]);

      if (profileResult.error) throw profileResult.error;
      if (coachLinkResult.error) throw coachLinkResult.error;
      if (onboardingResult.error) throw onboardingResult.error;
      if (transcriptUploadsResult.error) throw transcriptUploadsResult.error;
      if (processedMeetingsResult.error) throw processedMeetingsResult.error;

      const processedMeetingIds = new Set(
        (processedMeetingsResult.data ?? [])
          .map((row) => row.meeting_id)
          .filter((meetingId): meetingId is string => typeof meetingId === "string" && meetingId.length > 0),
      );

      return resolveSoloModeExperienceState({
        accountMode: profileResult.data?.account_mode ?? null,
        trialStartedAt: profileResult.data?.trial_started_at ?? null,
        trialEndsAt: profileResult.data?.trial_ends_at ?? null,
        hasCoachConnection: Boolean(coachLinkResult.data?.coach_id),
        leadershipLens: onboardingResult.data?.leadership_lens ?? null,
        transcriptUploadsUsed: transcriptUploadsResult.count ?? null,
        processedMeetingsUsed: processedMeetingIds.size,
      });
    },
  });

  if (authLoading) {
    return {
      ...DEFAULT_SOLO_MODE_EXPERIENCE_STATE,
      isLoading: true,
    };
  }

  if (!user?.id) {
    return DEFAULT_SOLO_MODE_EXPERIENCE_STATE;
  }

  if (isPreviewUser) {
    return {
      ...DEFAULT_SOLO_MODE_EXPERIENCE_STATE,
      hasCoachConnection: true,
      accountMode: "coach_led",
    };
  }

  return {
    ...DEFAULT_SOLO_MODE_EXPERIENCE_STATE,
    ...query.data,
    isLoading: query.isLoading,
  };
}

function buildRemainingCount(isSoloModeLeader: boolean, used: number | null | undefined, limit: number | null) {
  if (!isSoloModeLeader || typeof used !== "number" || typeof limit !== "number") return null;
  return Math.max(0, limit - used);
}
